import axios from "axios";
import { IS_LOADING, SNACKBAR_OPEN } from "../reducers";
import config from "./Config";

axios.defaults.baseURL = config.API_URL;
axios.defaults.timeout = 0;

export const ApiUtils = {
  axios: axios.create({ baseURL: config.API_URL, timeout: 0 }),
  dispatch: null,
  state: null,
  navigate: null,
};

const changeLoaderStatus = () => {
  ApiUtils.dispatch({ type: IS_LOADING, payload: { isLoad: false } });
  ApiUtils.dispatch({ type: "IS_NOT_LOADING", payload: true });
};

/**
 * Read the access token out of localStorage.
 * Returns null when there is no usable token, so callers can omit the
 * Authorization header entirely rather than sending "Bearer undefined".
 */
export const readAccessToken = () => {
  try {
    const token = JSON.parse(localStorage.getItem("token")) || {};
    return token.access || token.access_token || null;
  } catch (e) {
    return null;
  }
};

/**
 * Pull the Authorization header back out of a request config.
 * axios 1.x wraps `config.headers` in an AxiosHeaders instance whose key
 * casing is not guaranteed, so probe every form rather than assuming one.
 */
const getAuthHeader = (cfg) => {
  const headers = cfg && cfg.headers;
  if (!headers) return null;
  if (typeof headers.get === "function") {
    const viaGetter = headers.get("Authorization");
    if (viaGetter) return viaGetter;
  }
  return headers.Authorization || headers.authorization || null;
};

/**
 * Diagnostic only: describe the token that was actually put on the wire so a
 * 401 can be attributed without guessing (expired vs malformed vs absent).
 */
const describeAuthHeader = (authHeader) => {
  if (!authHeader) return "none sent";
  const raw = String(authHeader).replace(/^Bearer\s+/i, "");
  if (!raw || raw === "undefined" || raw === "null") {
    return `BOGUS literal "${raw}"`;
  }
  try {
    const part = raw.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(part));
    const exp = payload.exp ? new Date(payload.exp * 1000) : null;
    return [
      `user_id=${payload.user_id}`,
      `exp=${exp ? exp.toISOString() : "?"}`,
      `expired=${exp ? exp.getTime() < Date.now() : "?"}`,
      `tail=…${raw.slice(-8)}`,
    ].join(" ");
  } catch (e) {
    return `UNPARSEABLE JWT tail=…${raw.slice(-8)}`;
  }
};

const report401 = (error) => {
  const cfg = error.config || {};
  /* eslint-disable no-console */
  console.warn(
    `[auth] 401 on ${String(cfg.method || "?").toUpperCase()} ` +
      `${cfg.baseURL || ""}${cfg.url || "?"}\n` +
      `       token sent : ${describeAuthHeader(getAuthHeader(cfg))}\n` +
      `       server said: ${error.response && error.response.data && error.response.data.error}`
  );
  /* eslint-enable no-console */
};

// Add a request interceptor
ApiUtils.axios.interceptors.request.use(
  function (config) {
    const access = readAccessToken();
    // Only send Authorization when we actually hold a token. Sending
    // "Bearer undefined" makes DRF reject even AllowAny endpoints, because
    // APIView.initial() authenticates on every request regardless of
    // permission class.
    if (access) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${access}`,
      };
    }
    ApiUtils.dispatch({
      type: IS_LOADING,
      payload: { isLoad: ApiUtils.state.loaderState },
    });
    return config;
  },
  function (error) {
    changeLoaderStatus();
    if (!error.response) {
      ApiUtils.dispatch({
        type: SNACKBAR_OPEN,
        payload: { isNotify: true, severity: "error", message: error.message },
      });
      return Promise.reject(new Error(error.message || "Request failed"));
    }
    let message =
      error?.response?.data?.error || error?.response?.data?.errors?.join(", ");
    // Do something with request error
    ApiUtils.dispatch({
      type: SNACKBAR_OPEN,
      payload: { isNotify: true, severity: "error", message: message },
    });
    return Promise.reject(new Error(message || "Request failed"));
  }
);

ApiUtils.axios.interceptors.response.use(
  function (response) {
    changeLoaderStatus();
    return response;
  },
  function (error) {
    changeLoaderStatus();
    if (!error.response) {
      ApiUtils.dispatch({
        type: SNACKBAR_OPEN,
        payload: { isNotify: true, severity: "error", message: error.message },
      });
      return Promise.reject(new Error(error.message || "Request failed"));
    }
    if (error.response.status === 401) {
      report401(error);
      const sent = getAuthHeader(error.config);
      const sentToken = sent ? String(sent).replace(/^Bearer\s+/i, "") : null;
      // Tear the session down only when the request that failed carried the
      // token we are still holding. A 401 for a superseded token -- a request
      // issued before a fresh login replaced it, which is the race behind the
      // intermittent logouts -- says nothing about the current session.
      //
      // Deliberately no redirect here. Navigation belongs to the route guards,
      // which know whether the user came from /login or /agency-login; a hard
      // window.location change from inside an interceptor reloads the app and
      // strands the user on the wrong login page.
      if (sentToken && sentToken === readAccessToken()) {
        sessionStorage.clear();
        localStorage.clear();
        ApiUtils.dispatch({ type: "LOGOUT" });
      }
    }
    let message =
      error?.response?.data?.error || error?.response?.data?.errors?.join(", ");
    ApiUtils.dispatch({
      type: SNACKBAR_OPEN,
      payload: { isNotify: true, severity: "error", message: message },
    });
    return Promise.reject(new Error(message || "Request failed"));
  }
);


const baseApi = (url, method, params, data, responseType, header) =>
  ApiUtils.axios.request({
    url,
    method,
    params,
    data,
    responseType,
    header
  });

export default baseApi;
